import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { useAuth0 } from "src/contexts/auth0Provider";
import { useAuth } from "src/contexts/global/GlobalContext";
import { hasAdminOrPowerRole } from "src/services/auth";
import { isChangelogEnabled } from "src/util/changelogFlag";
import {
  isTeamInsightsEnabled,
  isTeamOwnerEditable,
  isTeamOwnerEnabled,
} from "src/util/orgDesignDirectFlags";
import { isTeamInsightsAvailableForTeam } from "src/util/isTeamInsightsAvailable";
import { useSelectedOrgChartTeam } from "src/components/shared/TeamMetrics/useTeamMetrics";
import type { OrgChartState } from "../../types";

/** Release: enableOrgDesign. Beta: enableDemoODOptions nested under release.
 * enableOrgChartReportingLineView nests under beta (org-chart canvas flip only;
 * owner/member DnD menus use OD beta). */

export type FlagBag = {
  enableOrgDesign?: boolean | null | undefined;
  enableOrgChartAttributeOverlays?: boolean | null | undefined;
  enableOrgChartEditableAttributes?: boolean | null | undefined;
  enableDemoODOptions?: boolean | null | undefined;
  enableOrgChartReportingLineView?: boolean | null | undefined;
  enableDemoODInferredOwners?: boolean | null | undefined;
};

/** OD beta surfaces — requires release + Enable OD beta. */
export const isOrgDesignBetaEnabled = ({
  enableOrgDesign,
  enableDemoODOptions,
}: FlagBag): boolean => Boolean(enableOrgDesign && enableDemoODOptions);

export const useOrgDesignBetaEnabled = (): boolean => {
  const { enableOrgDesign, enableDemoODOptions } = useWorkspaceFeatureFlags();
  return isOrgDesignBetaEnabled({ enableOrgDesign, enableDemoODOptions });
};

/** Org-chart reporting-line view + DnD — OD beta nested flag. */
export const isOrgChartReportingLineViewEnabled = ({
  enableOrgDesign,
  enableDemoODOptions,
  enableOrgChartReportingLineView,
}: FlagBag): boolean =>
  Boolean(
    isOrgDesignBetaEnabled({ enableOrgDesign, enableDemoODOptions }) &&
    enableOrgChartReportingLineView
  );

export const useOrgChartReportingLineViewEnabled = (): boolean => {
  const {
    enableOrgDesign,
    enableDemoODOptions,
    enableOrgChartReportingLineView,
  } = useWorkspaceFeatureFlags();
  return isOrgChartReportingLineViewEnabled({
    enableOrgDesign,
    enableDemoODOptions,
    enableOrgChartReportingLineView,
  });
};

/** Position-backed vacancies — gates position grouping (grey boxes) in the chart. */
export const useOrgChartPositionsEnabled = (): boolean => {
  const { enablePositionBackedVacancies } = useWorkspaceFeatureFlags();
  return Boolean(enablePositionBackedVacancies);
};

/** Add-open-role gate: the vacant-role toggle, plus position creation rights
 * when positions back vacancies. Callers add their surface-specific checks. */
export const useCanAddOpenRole = (
  enableAddVacantRole: boolean | null | undefined
): boolean => {
  const positionsEnabled = useOrgChartPositionsEnabled();
  const canCreatePositions = useCanCreatePositions();
  return (
    Boolean(enableAddVacantRole) && (!positionsEnabled || canCreatePositions)
  );
};

/** Position creation — the addPosition mutation is ADMIN/POWER only. */
export const useCanCreatePositions = (): boolean => {
  const positionsEnabled = useOrgChartPositionsEnabled();
  const auth = useAuth();
  return positionsEnabled && hasAdminOrPowerRole(auth?.user ?? auth);
};

/** Team Insights tab, route, and org chart insights panel. */
export const useTeamInsightsFeatureEnabled = (): boolean =>
  isTeamInsightsEnabled(useWorkspaceFeatureFlags());

/** Team owner on the header, org chart, details panel, and person "Manager of". */
export const useTeamOwnerEnabled = (): boolean =>
  isTeamOwnerEnabled(useWorkspaceFeatureFlags());

/** Editable owner control. Read-only when this is false and the feature is on. */
export const useTeamOwnerEditable = (
  team: Parameters<typeof isTeamOwnerEditable>[1],
  teamsById?: Parameters<typeof isTeamOwnerEditable>[2]
): boolean => isTeamOwnerEditable(useWorkspaceFeatureFlags(), team, teamsById);

/** Inferred owner stand-ins — OD beta + nested inferred-owners flag. */
export const useShowInferredOwnersFeatureEnabled = (): boolean => {
  const { enableOrgDesign, enableDemoODOptions, enableDemoODInferredOwners } =
    useWorkspaceFeatureFlags();
  return Boolean(
    isOrgDesignBetaEnabled({ enableOrgDesign, enableDemoODOptions }) &&
    enableDemoODInferredOwners
  );
};

/** Direct gate for the changelog tab, route, and org chart summary. */
export const useChangelogTabEnabled = (): boolean =>
  isChangelogEnabled(useWorkspaceFeatureFlags());

/** Attribute overlays (Properties people/team attrs + GQL @includes). */
export const isOrgChartAttributeOverlaysEnabled = ({
  enableOrgDesign,
  enableOrgChartAttributeOverlays,
}: Pick<
  FlagBag,
  "enableOrgDesign" | "enableOrgChartAttributeOverlays"
>): boolean => Boolean(enableOrgDesign && enableOrgChartAttributeOverlays);

export const useOrgChartAttributeOverlaysEnabled = (): boolean => {
  const { enableOrgDesign, enableOrgChartAttributeOverlays } =
    useWorkspaceFeatureFlags();
  return isOrgChartAttributeOverlaysEnabled({
    enableOrgDesign,
    enableOrgChartAttributeOverlays,
  });
};

/** Details-panel inline attribute editing (Org Design settings toggle). */
export const isOrgChartEditableAttributesEnabled = ({
  enableOrgDesign,
  enableOrgChartAttributeOverlays,
  enableOrgChartEditableAttributes,
}: Pick<
  FlagBag,
  | "enableOrgDesign"
  | "enableOrgChartAttributeOverlays"
  | "enableOrgChartEditableAttributes"
>): boolean =>
  Boolean(
    isOrgChartAttributeOverlaysEnabled({
      enableOrgDesign,
      enableOrgChartAttributeOverlays,
    }) && enableOrgChartEditableAttributes
  );

export const useOrgChartEditableAttributesEnabled = (): boolean => {
  const {
    enableOrgDesign,
    enableOrgChartAttributeOverlays,
    enableOrgChartEditableAttributes,
  } = useWorkspaceFeatureFlags();
  return isOrgChartEditableAttributesEnabled({
    enableOrgDesign,
    enableOrgChartAttributeOverlays,
    enableOrgChartEditableAttributes,
  });
};

export const useMetricsPanelEnabled = (state: OrgChartState): boolean => {
  const isFeatureEnabled = useTeamInsightsFeatureEnabled();
  const workspace = useWorkspace();
  const { currentTeam } = useSelectedOrgChartTeam(state);
  if (!currentTeam?.id || !currentTeam?.type) {
    return false;
  }
  return isTeamInsightsAvailableForTeam({
    insightsEnabled: isFeatureEnabled,
    team: currentTeam,
    groupTypes: workspace?.config?.groupTypes,
  });
};

export type AuthUser = Parameters<typeof hasAdminOrPowerRole>[0];

export const isOrgChartEditModeControlsEnabled = ({
  enableOrgDesign,
  user,
}: {
  enableOrgDesign: boolean;
  user: AuthUser;
}): boolean => Boolean(enableOrgDesign) && hasAdminOrPowerRole(user);

export const useOrgChartEditModeControlsEnabled = (): boolean => {
  const { enableOrgDesign } = useWorkspaceFeatureFlags();
  const { user } = useAuth0();
  return isOrgChartEditModeControlsEnabled({ enableOrgDesign, user });
};
